import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSuperAdmin } from "@/contexts/SuperAdminContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function SuperAdminLogin() {
  const { login } = useSuperAdmin();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate("/superadmin/accounts");
    } else {
      setError(result.error || "Invalid credentials");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0d1117] relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="fixed -top-1/5 -left-1/10 w-[600px] h-[600px] rounded-full bg-purple-700/10 blur-3xl pointer-events-none" />
      <div className="fixed -bottom-1/5 -right-1/10 w-[500px] h-[500px] rounded-full bg-blue-600/8 blur-3xl pointer-events-none" />

      <Card className="w-full max-w-[420px] bg-white/[0.04] border border-white/[0.08] shadow-2xl backdrop-blur-xl rounded-2xl">
        <CardHeader className="pb-0 pt-10 px-10">
          <div className="flex flex-col items-center text-center gap-3 mb-2">
            <img
              src="/besthr.png"
              alt="BestHR"
              className="h-12 w-auto object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-white mt-1">BestHR</h1>
              <p className="text-xs text-white/40 mt-1">Super Admin Console</p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-10 pb-10 pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Email Address
              </Label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="superadmin@besthr.app"
                className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/25 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Password
              </Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/25 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>

            {error && (
              <div className="px-3.5 py-2.5 rounded-lg bg-red-500/10 border border-red-500/25 text-red-300 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold py-5 rounded-lg shadow-lg shadow-purple-700/30 transition-all disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <p className="text-center mt-7 text-xs text-white/20">
            BestHR Platform Administration
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
